<template>
    <div class="glass-theme fade-in-page">
        <b-card class="glass-card">
            <!-- Header Section -->
            <div class="page-header">
                <div class="header-left">
                    <div class="header-icon-wrapper">
                        <i class="fas fa-chart-line"></i>
                    </div>
                    <div>
                        <h2 class="page-title">Discrepancy Management</h2>
                        <p class="page-subtitle">Track and reconcile payment discrepancies</p>
                    </div>
                </div>
                 <div class="stats-grid">
                <div class="stat-item">
                    <div class="stat-icon discrepancy">
                        <i class="fas fa-exclamation-triangle"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-label">Total Shortage</span>
                        <div class="stat-value discrepancy-value">${{ formatNumber(totalDiscrepancyAmount) }}</div>
                    </div>
                   
                </div>

                <div class="stat-item">
                    <div class="stat-icon expected">
                        <i class="fas fa-chart-line"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-label">Expected Revenue</span>
                        <div class="stat-value">${{ formatNumber(totalExpectedAmount) }}</div>
                    </div>
                </div>

                <div class="stat-item">
                    <div class="stat-icon actual">
                        <i class="fas fa-check-circle"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-label">Actual Collection</span>
                        <div class="stat-value">${{ formatNumber(totalActualAmount) }}</div>
                    </div>
                </div>

                <div class="stat-item">
                    <div class="stat-icon count">
                        <i class="fas fa-file-invoice"></i>
                    </div>
                    <div class="stat-content">
                        <span class="stat-label">Total Orders</span>
                        <div class="stat-value">{{ filteredItems.length }}</div>
                    </div>
                </div>
            </div>
            </div>

            <!-- Loading Overlay -->
            <transition name="fade">
                <div v-if="apiLoading" class="custom-loader">
                    <div class="loader-container">
                        <div class="loader-spinner"></div>
                        <p class="loader-text">{{ loadingMessage }}</p>
                        <div class="loader-progress-container">
                            <div class="loader-progress-bar" :style="{ width: loadingProgress + '%' }"></div>
                        </div>
                        <p class="loader-tip">{{ currentTip }}</p>
                    </div>
                </div>
            </transition>

            <!-- Stats Grid -->
           

            <!-- Advanced Filters -->
            <div class="filters-wrapper">
                <div class="filters-header">
                    <div class="filters-title">
                        <i class="fas fa-sliders-h"></i>
                        <span>Smart Filters</span>
                    </div>
                    <button class="reset-filters" @click="resetFilters">
                        <i class="fas fa-undo-alt"></i>
                        Reset
                    </button>
                </div>
                <div style="display:flex;  flex-wrap:wrap;">

                <div class="filters-grid">
                    <div class="filter-field">
                        <label><i class="far fa-calendar-alt"></i> Date Range</label>
                        <div class="date-picker-wrapper">
                            <input type="text" id="date-range" v-model="dateRangeText" placeholder="Select dates" readonly>
                            <i class="fas fa-calendar picker-icon"></i>
                        </div>
                    </div>

                    <div class="filter-field">
                        <label><i class="fas fa-store"></i> Location</label>
                        <select v-model="temp_filters.location_id" @change="applyFilters">
                            <option :value="null">All Locations</option>
                            <option v-for="loc in locationOptions" :key="loc.value" :value="loc.value">
                                {{ loc.text }}
                            </option>
                        </select>
                    </div>

                    <div class="filter-field">
                        <label><i class="fas fa-credit-card"></i> Payment Type</label>
                        <select v-model="temp_filters.payment_method" @change="applyFilters">
                            <option :value="null">All Types</option>
                            <option value="cash">💵 Cash</option>
                            <option value="cc">💳 Credit Card</option>
                            <option value="finance">🏦 Finance</option>
                            <option value="store_credit">🎁 Store Credit</option>
                        </select>
                    </div>

                    <div class="filter-field">
                        <label><i class="fas fa-dollar-sign"></i> Minimum Shortage</label>
                        <div class="amount-input">
                            <span class="currency">$</span>
                            <input type="number" step="0.01" v-model="temp_filters.min_short_amount" placeholder="0.00" @input="applyFilters">
                        </div>
                    </div>

                    <div class="filter-field search-field">
                        <label><i class="fas fa-search"></i> Search Invoice</label>
                        <div class="search-input">
                            <input type="text" v-model="temp_filters.search_invoice" placeholder="Enter invoice ID..." @keyup.enter="applyFilters">
                            <button @click="applyFilters" class="search-btn">
                                <i class="fas fa-search"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="filters-actions">
                    <!-- <button class="btn-primary" @click="applyFilters">
                        <i class="fas fa-check"></i>
                        Apply Filters
                    </button> -->
                    <!-- <button class="btn-secondary" @click="exportToCSV">
                        <i class="fas fa-download"></i>
                        Export Data
                    </button> -->
                </div>
                </div>
            </div>

            <!-- Data Table -->
            <div class="table-wrapper" v-if="filteredItems.length > 0">
                <div class="table-header">
                  
                    <div class="table-stats">
                        Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} - {{ Math.min(currentPage * itemsPerPage, filteredItems.length) }} of {{ filteredItems.length }} entries
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th @click="sortby('invoice_id')" class="sortable">
                                    Invoice ID
                                    <i :class="getSortIcon('invoice_id')"></i>
                                </th>
                                 <th @click="sortby('salesperson_name')" class="sortable">
                                    Salesperson
                                    <i :class="getSortIcon('salesperson_name')"></i>
                                </th>

                                <th @click="sortby('fullname')" class="sortable">
                                    Customer
                                    <i :class="getSortIcon('fullname')"></i>
                                </th>
                                <th @click="sortby('created_date')" class="sortable">
                                    Date
                                    <i :class="getSortIcon('created_date')"></i>
                                </th>
                                <th @click="sortby('location_name')" class="sortable">
                                    Location
                                    <i :class="getSortIcon('location_name')"></i>
                                </th>
                                <th>Payment</th>
                                <th @click="sortby('expected_amount')" class="sortable text-right">
                                    Expected
                                    <i :class="getSortIcon('expected_amount')"></i>
                                </th>
                                <th @click="sortby('actual_amount')" class="sortable text-right">
                                    Actual
                                    <i :class="getSortIcon('actual_amount')"></i>
                                </th>
                                <th @click="sortby('short_amount')" class="sortable text-right">
                                    Shortage
                                    <i :class="getSortIcon('short_amount')"></i>
                                </th>
                                <th>Details</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in paginatedItems" :key="item.invoice_id" class="data-row">
                                <td class="invoice-id">
                                    <span class="id-badge">{{ item.invoice_id }}</span>
                                </td>
                                <td>{{ item.username || 'N/A' }}</td>
                                <td>
                                    <div class="customer-info">
                                        <strong>{{ item.fullname }}</strong>
                                        <span>{{ item.phone }}</span>
                                        <small>{{ item.email }}</small>
                                    </div>
                                </td>
                                <td>{{ formatDate(item.created_date) }}</td>
                                <td>{{ item.location_name }}</td>
                                <td>
                                    <span :class="['payment-type', item.payment_type]">
                                        <i :class="getPaymentIcon(item.payment_type)"></i>
                                        {{ item.payment_type ? item.payment_type.toUpperCase() : 'N/A' }}
                                    </span>
                                </td>
                                <td class="text-right amount expected">${{ formatNumber(item.expected_amount) }}</td>
                                <td class="text-right amount actual">${{ formatNumber(item.actual_amount) }}</td>
                                <td class="text-right">
                                    <span class="shortage-badge">
                                        <i class="fas fa-exclamation-circle"></i>
                                        ${{ formatNumber(item.short_amount) }}
                                    </span>
                                </td>
                                <td>
                                    <div class="payment-breakdown">
                                        <div v-for="(pm, idx) in item.payment_methods" :key="idx" class="payment-item">
                                            <span class="pm-type">{{ pm.value }}</span>
                                            <span class="pm-amount">${{ formatNumber(pm.amount) }}</span>
                                            <span class="pm-actual" v-if="pm.actual_amount">→ ${{ formatNumber(pm.actual_amount) }}</span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="action-group">
                                        <button class="action-icon view" @click="viewInvoice(item.invoice_id)" title="View Details">
                                            <i class="fas fa-eye"></i>
                                        </button>
                                        <button class="action-icon edit" @click="openAdjustModal(item)" title="Adjust Payment">
                                            <i class="fas fa-edit"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="pagination-wrapper">
                    <div class="pagination-info">
                        <select v-model="itemsPerPage" class="per-page-select">
                            <option :value="10">10 per page</option>
                            <option :value="25">25 per page</option>
                            <option :value="50">50 per page</option>
                            <option :value="100">100 per page</option>
                        </select>
                    </div>
                    <div class="pagination-controls">
                        <button @click="currentPage--" :disabled="currentPage === 1" class="page-nav">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <div class="page-numbers">
                            <button 
                                v-for="page in displayedPages" 
                                :key="page"
                                @click="currentPage = page"
                                :class="['page-number', { active: currentPage === page }]">
                                {{ page }}
                            </button>
                        </div>
                        <button @click="currentPage++" :disabled="currentPage === totalPages" class="page-nav">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
                <div class="empty-icon">
                    <i class="fas fa-check-circle"></i>
                </div>
                <h3>All Caught Up!</h3>
                <p>No payment discrepancies found in the current filters</p>
            </div>
        </b-card>

        <!-- Adjustment Modal -->
        <transition name="modal-fade">
            <div v-if="showAdjustModal" class="modal-overlay" @click.self="closeModal">
                <div class="modal-dialog">
                    <div class="modal-content-modern">
                        <div class="modal-header-modern">
                            <h4>
                                <i class="fas fa-calculator"></i>
                                Adjust Payment Amount
                            </h4>
                            <button class="modal-close" @click="closeModal">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <div class="modal-body-modern" v-if="selectedInvoice">
                            <div class="invoice-banner">
                                <i class="fas fa-file-invoice"></i>
                                <span>{{ selectedInvoice.invoice_id }}</span>
                            </div>
                            
                            <div v-for="(pm, idx) in selectedInvoice.payment_methods" :key="idx" class="payment-card">
                                <div class="payment-card-header">
                                    <span :class="['payment-badge', pm.value]">
                                        <i :class="getPaymentIcon(pm.value)"></i>
                                        {{ pm.value.toUpperCase() }}
                                    </span>
                                </div>
                                <div class="payment-card-body">
                                    <div class="amount-row">
                                        <div class="amount-label">Expected</div>
                                        <div class="amount-value expected">${{ formatNumber(pm.amount) }}</div>
                                    </div>
                                    <div class="amount-row">
                                        <div class="amount-label">Actual</div>
                                        <div class="amount-value">
                                            <input type="number" step="0.01" v-model="pm.actual_amount" class="amount-input-modern">
                                        </div>
                                    </div>
                                    <div class="amount-row">
                                        <div class="amount-label">Shortage</div>
                                        <div class="amount-value shortage" :class="getShortageClass(pm)">
                                            ${{ formatNumber((pm.amount || 0) - (pm.actual_amount || pm.amount || 0)) }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer-modern">
                            <button class="btn-cancel-modern" @click="closeModal">Cancel</button>
                            <button class="btn-save-modern" @click="saveAdjustment">Save Changes</button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import moment from "moment"
    import Requestor from "@/utilities/Requestor"
    import Lightpick from 'lightpick';
    import 'lightpick/css/lightpick.css';

    export default {
        name: "AdminToolsDiscrepancyInvoices",
        data: function() {
            return {
                packet: {},
                items: [],
                filteredItems: [],
                sort_asc: true,
                sort_key: 'invoice_id',
                currentPage: 1,
                itemsPerPage: 25,
                apiLoading: false,
                loadingMessage: 'Loading discrepancy data',
                loadingProgress: 0,
                currentTipIndex: 0,
                loadingInterval: null,
                tipInterval: null,
                msgInterval: null,
                apiCallStack: 0,
                showAdjustModal: false,
                selectedInvoice: null,
                dateRangeText: '',
                startDate: null,
                endDate: null,
                temp_filters: {
                    location_id: null,
                    payment_method: null,
                    min_short_amount: null,
                    search_invoice: null
                },
                active_filters: {
                    location_id: null,
                    payment_method: null,
                    min_short_amount: null,
                    search_invoice: null
                },
                locationsList: [],
                locationOptions: [],
                tips: [
                    "💡 Tip: Sort by shortage amount to see largest discrepancies first",
                    "📊 Use date filters to track weekly or monthly trends",
                    "💰 Total shortage represents potential revenue loss",
                    "🔍 Search by invoice ID for quick lookup",
                    "📈 Export data for external analysis and reporting"
                ]
            }
        },
        computed: {
            currentTip() {
                return this.tips[this.currentTipIndex] || this.tips[0];
            },
            totalExpectedAmount() {
                return this.filteredItems.reduce((sum, item) => sum + (item.expected_amount || 0), 0);
            },
            totalActualAmount() {
                return this.filteredItems.reduce((sum, item) => sum + (item.actual_amount || 0), 0);
            },
            totalDiscrepancyAmount() {
                return this.filteredItems.reduce((sum, item) => sum + (item.short_amount || 0), 0);
            },
            totalPages() {
                return Math.ceil(this.filteredItems.length / this.itemsPerPage);
            },
            displayedPages() {
                const total = this.totalPages;
                const current = this.currentPage;
                const delta = 2;
                const range = [];
                const rangeWithDots = [];
                let l;

                for (let i = 1; i <= total; i++) {
                    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                        range.push(i);
                    }
                }

                range.forEach((i) => {
                    if (l) {
                        if (i - l === 2) {
                            rangeWithDots.push(l + 1);
                        } else if (i - l !== 1) {
                            rangeWithDots.push('...');
                        }
                    }
                    rangeWithDots.push(i);
                    l = i;
                });

                return rangeWithDots;
            },
            paginatedItems() {
                const start = (this.currentPage - 1) * this.itemsPerPage;
                const end = start + this.itemsPerPage;
                return this.filteredItems.slice(start, end);
            }
        },
        methods: {
            formatNumber(value) {
                if (value === null || value === undefined) return '0.00';
                return parseFloat(value).toFixed(2);
            },
            formatDate(date) {
                if (!date) return 'N/A';
                return moment(date).format('MMM DD, YYYY');
            },
            getSortIcon(key) {
                if (this.sort_key !== key) return 'fas fa-sort';
                return this.sort_asc ? 'fas fa-sort-up' : 'fas fa-sort-down';
            },
            getPaymentIcon(type) {
                const icons = {
                    'cash': 'fas fa-money-bill-wave',
                    'cc': 'fas fa-credit-card',
                    'finance': 'fas fa-building',
                    'store_credit': 'fas fa-gift'
                };
                return icons[type] || 'fas fa-tag';
            },
            getShortageClass(pm) {
                const short = (pm.amount || 0) - (pm.actual_amount || pm.amount || 0);
                if (short > 0) return 'shortage-negative';
                if (short < 0) return 'shortage-positive';
                return '';
            },
            sortby(key) {
                if (this.sort_key === key) {
                    this.sort_asc = !this.sort_asc;
                } else {
                    this.sort_key = key;
                    this.sort_asc = true;
                }
                
                this.filteredItems.sort((a, b) => {
                    let valA = a[key];
                    let valB = b[key];
                    
                    if (typeof valA === 'string') valA = valA.toLowerCase();
                    if (typeof valB === 'string') valB = valB.toLowerCase();
                    
                    if (valA < valB) return this.sort_asc ? -1 : 1;
                    if (valA > valB) return this.sort_asc ? 1 : -1;
                    return 0;
                });
            },
            applyFilters() {
                this.active_filters = { ...this.temp_filters };
                this.filterInvoices();
                this.currentPage = 1;
            },
            resetFilters() {
                this.temp_filters = {
                    location_id: null,
                    payment_method: null,
                    min_short_amount: null,
                    search_invoice: null
                };
                this.active_filters = { ...this.temp_filters };
                this.startDate = null;
                this.endDate = null;
                this.dateRangeText = '';
                this.filterInvoices();
                this.currentPage = 1;
            },
            filterInvoices() {
                let filtered = [...this.items];
                
                if (this.startDate && this.endDate) {
                    filtered = filtered.filter(item => {
                        const itemDate = moment(item.created_date);
                        return itemDate.isBetween(this.startDate, this.endDate, 'day', '[]');
                    });
                }
                
                if (this.active_filters.location_id) {
                    filtered = filtered.filter(item => item.location_id === this.active_filters.location_id);
                }
                
                if (this.active_filters.payment_method) {
                    filtered = filtered.filter(item => item.payment_type === this.active_filters.payment_method);
                }
                
                if (this.active_filters.min_short_amount && this.active_filters.min_short_amount > 0) {
                    filtered = filtered.filter(item => item.short_amount >= this.active_filters.min_short_amount);
                }
                
                if (this.active_filters.search_invoice) {
                    const searchTerm = this.active_filters.search_invoice.toUpperCase();
                    filtered = filtered.filter(item => item.invoice_id.toUpperCase().includes(searchTerm));
                }
                
                this.filteredItems = filtered;
            },
            exportToCSV() {
                const headers = ['Invoice ID', 'Customer', 'Phone', 'Email', 'Date', 'Location', 'Payment Type', 'Expected Amount', 'Actual Amount', 'Short Amount'];
                const rows = this.filteredItems.map(item => [
                    item.invoice_id,
                    item.fullname,
                    item.phone,
                    item.email,
                    this.formatDate(item.created_date),
                    item.location_name,
                    item.payment_type,
                    this.formatNumber(item.expected_amount),
                    this.formatNumber(item.actual_amount),
                    this.formatNumber(item.short_amount)
                ]);
                
                const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `discrepancy_report_${moment().format('YYYY-MM-DD')}.csv`;
                a.click();
                URL.revokeObjectURL(url);
                
                this.$bvToast.toast("Report exported successfully", {title: "Success", variant: "success", autoHideDelay: 1500});
            },
            viewInvoice(invoiceId) {
    const routeData = this.$router.resolve({
        path: `/sales/viewsale/${invoiceId}/`,
    });
    window.open(routeData.href, "_blank", "noopener,noreferrer");
},
            openAdjustModal(item) {
                this.selectedInvoice = JSON.parse(JSON.stringify(item));
                this.showAdjustModal = true;
            },
            closeModal() {
                this.showAdjustModal = false;
                this.selectedInvoice = null;
            },
            saveAdjustment() {
                let that = this;
                let path = "/api/sales/update_payment_method/";
                let data = {
                    invoice_id: this.selectedInvoice.invoice_id,
                    payment_methods: this.selectedInvoice.payment_methods
                };
                
                let s = function(response) {
                    if (response.data && response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", variant: "warning", autoHideDelay: 1500});
                    } else {
                        that.$bvToast.toast("Payment updated successfully", {title: "Success", variant: "success", autoHideDelay: 1500});
                        that.closeModal();
                        that.loadDiscrepancyInvoices();
                    }
                };
                
                let f = function(error) {
                    that.$bvToast.toast("Error updating payment", {title: "Error", variant: "danger", autoHideDelay: 1500});
                };
                
                Requestor.postAsyncP(path, data, this, s, f);
            },
            clearDates() {
                this.startDate = null;
                this.endDate = null;
                this.dateRangeText = '';
                this.filterInvoices();
            },
            
            async fetchLocations() {
                try {
                    const response = await new Promise((resolve, reject) => {
                        Requestor.getSyncP("/api/location/get_all_locations/", {}, this, resolve, reject);
                    });
                    
                    let locations = [];
                    if (response.data && response.data.result) {
                        locations = response.data.result;
                    } else if (response && response.result) {
                        locations = response.result;
                    }
                    
                    this.locationsList = locations;
                    this.locationOptions = this.locationsList.map(loc => ({
                        value: loc.location_id || loc.id,
                        text: loc.location_name || loc.name || `Location ${loc.location_id || loc.id}`
                    })).sort((a, b) => a.text.localeCompare(b.text));
                    
                    this.packet.locations_dict = {};
                    this.locationsList.forEach(loc => {
                        const locId = loc.location_id || loc.id;
                        const locName = loc.location_name || loc.name;
                        this.packet.locations_dict[locId] = locName;
                    });
                    
                } catch (e) {
                    console.error('Failed to fetch locations:', e);
                }
            },
            
            getLocationName(locationId) {
                if (this.packet.locations_dict && this.packet.locations_dict[locationId]) {
                    return this.packet.locations_dict[locationId];
                }
                return `Location ${locationId}`;
            },
            
            startApiLoading() {
                this.apiCallStack++;
                if (!this.apiLoading) {
                    this.apiLoading = true;
                    this.loadingProgress = 0;
                    this.currentTipIndex = 0;
                    
                    this.loadingInterval = setInterval(() => {
                        if (this.loadingProgress < 90) {
                            this.loadingProgress += Math.random() * 10;
                        }
                    }, 300);
                    
                    this.tipInterval = setInterval(() => {
                        if (this.currentTipIndex < this.tips.length - 1) {
                            this.currentTipIndex++;
                        }
                    }, 3000);
                }
            },
            stopApiLoading() {
                this.apiCallStack--;
                if (this.apiCallStack <= 0 && this.apiLoading) {
                    clearInterval(this.loadingInterval);
                    clearInterval(this.tipInterval);
                    this.loadingProgress = 100;
                    setTimeout(() => {
                        this.apiLoading = false;
                    }, 500);
                }
            },
            
            loadDiscrepancyInvoices() {
                this.startApiLoading();
                let that = this;
                let path = "/api/sales/get_discrepancy_invoices/";
                
                let s = function(response) {
                    if (response.data && response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", variant: "warning"});
                        that.items = [];
                    } else {
                        const invoiceData = response.data.result || response.result || [];
                        that.items = invoiceData;
                        
                        that.items.forEach(item => {
                            item.expected_amount = 0;
                            item.actual_amount = 0;
                            item.short_amount = 0;
                            item.payment_type = null;
                            
                            if (item.payment_methods) {
                                let paymentMethods = item.payment_methods;
                                if (typeof paymentMethods === 'string') {
                                    try {
                                        paymentMethods = JSON.parse(paymentMethods);
                                    } catch(e) {
                                        paymentMethods = [];
                                    }
                                }
                                
                                paymentMethods.forEach(pm => {
                                    const expected = parseFloat(pm.amount) || 0;
                                    const actual = (pm.actual_amount !== undefined && pm.actual_amount !== null) 
                                        ? parseFloat(pm.actual_amount) 
                                        : expected;
                                    const short = expected - actual;
                                    
                                    if (short > 0) {
                                        item.expected_amount += expected;
                                        item.actual_amount += actual;
                                        item.short_amount += short;
                                        if (!item.payment_type) item.payment_type = pm.value;
                                    }
                                });
                            }
                            
                            if (item.customer) {
                                const customer = typeof item.customer === 'string' ? JSON.parse(item.customer) : item.customer;
                                item.fullname = customer.fullname || 'N/A';
                                item.phone = customer.phone || 'N/A';
                                item.email = customer.email || 'N/A';
                            }
                            
                            item.created_date = item.ts;
                            item.location_name = that.getLocationName(item.location_id);
                        });
                        
                        that.items = that.items.filter(item => item.short_amount > 0);
                        that.filterInvoices();
                    }
                    that.stopApiLoading();
                };
                
                let f = function(error) {
                    that.$bvToast.toast("Error loading discrepancy data", {title: "Error", variant: "danger"});
                    that.items = [];
                    that.stopApiLoading();
                };
                
                Requestor.getSync(path, {}, s, f);
            }
        },
        async created() {
            this.packet = JSON.parse(localStorage.packet || '{}');
            this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
            await this.fetchLocations();
            this.loadDiscrepancyInvoices();
        },
        mounted() {
            this.$nextTick(() => {
                const picker = new Lightpick({
                    field: document.getElementById('date-range'),
                    singleDate: false,
                    repick: true,
                    onSelect: (start, end) => {
                        if (start) {
                            this.startDate = start.format('YYYY-MM-DD');
                            this.dateRangeText = start.format('MMM D, YYYY');
                            if (end) {
                                this.endDate = end.format('YYYY-MM-DD');
                                this.dateRangeText += ` - ${end.format('MMM D, YYYY')}`;
                            }
                            this.filterInvoices();
                        }
                    }
                });
            });
        },
        beforeDestroy() {
            if (this.loadingInterval) clearInterval(this.loadingInterval);
            if (this.tipInterval) clearInterval(this.tipInterval);
        }
    }
</script>

<style scoped>
/* Glass Theme - Preserved */
.glass-theme {
    position: relative;
    background: transparent !important;
    min-height: 100vh;
}

.glass-theme::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    filter: blur(10px);
    opacity: 0.5;
    z-index: -2;
    transform: scale(1.1);
}

.fade-in-page {
    animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

/* Glass Card */
.glass-card {
    background: rgba(255, 255, 255, 0.55) !important;
    backdrop-filter: blur(12px) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 24px !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
    padding: 24px !important;
    margin: 20px !important;
    transition: all 0.3s ease;
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    border-bottom: 2px solid rgba(0, 0, 0, 0.05);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.header-icon-wrapper {
    width: 56px;
    height: 56px;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: white;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 4px 0;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
}

.page-subtitle {
    font-size: 14px;
    color: #6c757d;
    margin: 0;
}

.discrepancy-badge {
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    border-radius: 16px;
    padding: 12px 12px;
    text-align: center;
    color: white;
}

.badge-count {
    font-size: 12px;
    font-weight: 500;
    display: block;
    line-height: 1;
}

.badge-label {
    font-size: 12px;
    opacity: 0.9;
}

/* Stats Grid */
.stats-grid {
    display: flex;
    gap: 20px;
    margin-bottom: 10px;
}

.stat-item {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    border-radius: 20px;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.stat-item:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 46px;
    height: 46px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: white;
}

.stat-icon.discrepancy { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.stat-icon.expected { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
.stat-icon.actual { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.stat-icon.count { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }

.stat-content {
  
}

.stat-label {
    font-size: 13px;
    color: #6c757d;
    display: block;
    margin-bottom: 4px;
}

.stat-value {
    font-size: 18px;
    font-weight: 700;
    color: #2d3748;
}

.discrepancy-value {
    color: #dc3545;
}

.stat-trend {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 4px 8px;
    border-radius: 20px;
}

.stat-trend.down {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}

/* Filters Wrapper */
.filters-wrapper {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(8px);
    border-radius: 20px;
    padding: 20px;
    margin-bottom: 32px;
}

.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
}

.filters-title i {
    margin-right: 8px;
    color: #667eea;
}

.reset-filters {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 8px;
    transition: all 0.2s;
}

.reset-filters:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #dc3545;
}

.filters-grid {
    display: flex;
    width:84%;
    gap: 16px;
    margin-bottom: 20px;
}

.filter-field label {
    font-size: 12px;
    font-weight: 600;
    color: #495057;
    display: block;
    margin-bottom: 8px;
}

.filter-field label i {
    margin-right: 6px;
    color: #667eea;
}

.filter-field select,
.filter-field input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    background: white;
    font-size: 14px;
    transition: all 0.2s;
}

.filter-field select:focus,
.filter-field input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.date-picker-wrapper {
    position: relative;
}

.date-picker-wrapper input {
    padding-right: 16px;
}

.picker-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #adb5bd;
    pointer-events: none;
}

.amount-input {
    position: relative;
}

.amount-input .currency {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-weight: 600;
}

.amount-input input {
    padding-left: 28px;
}

.search-input {
    display: flex;
    gap: 8px;
}

.search-input input {
    flex: 1;
}

.search-btn {
    padding: 0 16px;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    border: none;
    border-radius: 12px;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
}

.search-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.filters-actions {
    width:16%;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.btn-primary, .btn-secondary {
    padding: 10px 24px;
    border: none;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary {
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    color: white;
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
    background: rgba(0, 0, 0, 0.05);
    color: #495057;
}

.btn-secondary:hover {
    background: rgba(0, 0, 0, 0.1);
}

/* Table Styles */
.table-wrapper {
    background: white;
    border-radius: 20px;
    overflow: hidden;
    margin-top: 24px;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: rgba(0, 0, 0, 0.02);
    border-bottom: 1px solid #f0f0f0;
}

.table-info {
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
}

.table-info i {
    margin-right: 8px;
    color: #ffffff;
}

.table-stats {
    font-size: 13px;
    color: #6c757d;
}

.table-responsive {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead th {
    padding: 16px 12px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #f0f0f0;
}

.sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;
}

.sortable:hover {
    color: #667eea;
}

.sortable i {
    margin-left: 6px;
    font-size: 11px;
}

.data-table tbody td {
    padding: 16px 12px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
}

.data-row:hover {
    background: rgba(102, 126, 234, 0.04);
}

.invoice-id .id-badge {
    display: inline-block;
    padding: 12px 12px;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    color: white;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.customer-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.customer-info strong {
    font-size: 14px;
    color: #2d3748;
}

.customer-info span {
    font-size: 12px;
    color: #6c757d;
}

.customer-info small {
    font-size: 11px;
    color: #adb5bd;
}

.payment-type {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.payment-type.cash { background: #d4edda; color: #155724; }
.payment-type.cc { background: #e2e3e5; color: #383d41; }
.payment-type.finance { background: #d1ecf1; color: #0c5460; }
.payment-type.store_credit { background: #fff3cd; color: #856404; }

.text-right {
    text-align: right;
}

.amount {
    font-weight: 600;
}

.amount.expected {
    color: #555555;
}

.amount.actual {
    color: #5e5e5e;
}

.shortage-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
    padding: 6px 12px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 13px;
}

.payment-breakdown {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.payment-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.pm-type {
    display: inline-block;
    padding: 2px 8px;
    background: rgba(0, 0, 0, 0.05);
    border-radius: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.pm-amount {
    color: #6c757d;
}

.pm-actual {
    color: #dc3545;
}

.action-group {
    display: flex;
    gap: 8px;
}

.action-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.action-icon.view {
    background: rgba(23, 162, 184, 0.1);
    color: #17a2b8;
}

.action-icon.view:hover {
    background: #17a2b8;
    color: white;
}

.action-icon.edit {
    background: rgba(255, 193, 7, 0.1);
    color: #ffc107;
}

.action-icon.edit:hover {
    background: #ffc107;
    color: white;
}

/* Pagination */
.pagination-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-top: 1px solid #f0f0f0;
}

.per-page-select {
    padding: 6px 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: white;
    font-size: 13px;
    cursor: pointer;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.page-nav {
    width: 36px;
    height: 36px;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.page-nav:hover:not(:disabled) {
    background: #667eea;
    border-color: #667eea;
    color: white;
}

.page-nav:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-numbers {
    display: flex;
    gap: 4px;
}

.page-number {
    min-width: 36px;
    height: 36px;
    padding: 0 8px;
    border: 1px solid #dee2e6;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.page-number:hover {
    background: #f8f9fa;
}

.page-number.active {
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    color: white;
    border-color: #667eea;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
}

.empty-icon {
    font-size: 80px;
    color: #28a745;
    margin-bottom: 20px;
}

.empty-state h3 {
    font-size: 20px;
    color: #2d3748;
    margin-bottom: 8px;
}

.empty-state p {
    color: #6c757d;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

.modal-dialog {
    width: 90%;
    max-width: 560px;
    animation: slideUp 0.3s ease;
}

@keyframes slideUp {
    from {
        transform: translateY(50px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.modal-content-modern {
    background: white;
    border-radius: 24px;
    overflow: hidden;
}

.modal-header-modern {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    color: white;
}

.modal-header-modern h4 {
    margin: 0;
    font-size: 18px;
}

.modal-header-modern h4 i {
    margin-right: 8px;
}

.modal-close {
    background: none;
    border: none;
    color: white;
    font-size: 20px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.modal-close:hover {
    opacity: 1;
}

.modal-body-modern {
    padding: 24px;
    max-height: 60vh;
    overflow-y: auto;
}

.invoice-banner {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 12px 16px;
    border-radius: 12px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 600;
    color: #495057;
}

.payment-card {
    border: 1px solid #f0f0f0;
    border-radius: 16px;
    margin-bottom: 16px;
    overflow: hidden;
}

.payment-card-header {
    padding: 12px 16px;
    background: #f8f9fa;
    border-bottom: 1px solid #f0f0f0;
}

.payment-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.payment-badge.cash { background: #d4edda; color: #155724; }
.payment-badge.cc { background: #e2e3e5; color: #383d41; }
.payment-badge.finance { background: #d1ecf1; color: #0c5460; }
.payment-badge.store_credit { background: #fff3cd; color: #856404; }

.payment-card-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.amount-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.amount-label {
    font-size: 13px;
    color: #6c757d;
}

.amount-value {
    font-size: 18px;
    font-weight: 600;
}

.amount-value.expected {
    color: #ffc107;
}

.amount-value.shortage {
    color: #dc3545;
}

.amount-input-modern {
    width: 150px;
    padding: 8px 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    font-size: 14px;
    text-align: right;
}

.shortage-negative {
    color: #dc3545;
}

.shortage-positive {
    color: #28a745;
}

.modal-footer-modern {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.btn-cancel-modern {
    padding: 8px 20px;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel-modern:hover {
    background: #e9ecef;
}

.btn-save-modern {
    padding: 8px 20px;
    background: linear-gradient(135deg, #000000 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-save-modern:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Loader */
.custom-loader {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.loader-container {
    background: white;
    border-radius: 24px;
    padding: 40px;
    text-align: center;
    min-width: 320px;
}

.loader-spinner {
    width: 60px;
    height: 60px;
    margin: 0 auto 20px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #000000;
    border-right: 3px solid #764ba2;
    border-bottom: 3px solid #f093fb;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loader-text {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 20px;
}

.loader-progress-container {
    width: 100%;
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 16px;
}

.loader-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb);
    transition: width 0.3s ease;
}

.loader-tip {
    font-size: 13px;
    color: #6c757d;
    margin: 0;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-active, .modal-fade-leave-active {
    transition: opacity 0.3s;
}

.modal-fade-enter, .modal-fade-leave-to {
    opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .glass-card {
        padding: 16px !important;
        margin: 10px !important;
    }
    
    .page-header {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }
    
    .header-left {
        flex-direction: column;
    }
    
    .stats-grid {
        grid-template-columns: 1fr;
    }
    
    .filters-grid {
        grid-template-columns: 1fr;
    }
    
    .filters-actions {
        flex-direction: column;
    }
    
    .pagination-wrapper {
        flex-direction: column;
        gap: 12px;
    }
    
    .table-header {
        flex-direction: column;
        gap: 8px;
        text-align: center;
      

    }
    
    .data-table {
        font-size: 12px;
    }

    .data-table thead th {
    background:black !important;
    color: white !important;
    }
    
    .data-table thead th,
    .data-table tbody td {
        padding: 8px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsDiscrepancyInvoices.vue